import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type City, type Building, type Cell, type Place } from './city';
import type { Vec } from '../destruction/structure';
import { TrafficSurface } from './surface-access';

export const RAIL_Y = GROUND + 6 * CELL + 0.12;
export interface RailSection {
  id: number;
  a: Vec;
  b: Vec;
  start: number;
  length: number;
  buildingId: number;
  cells: number[];
}
export interface RailStation {
  service?: string;
  interchange?: string;
  id: string;
  name: string;
  distance: number;
  point: Vec;
  direction: Vec;
  side: Vec;
  entrance: Vec;
  lift: Vec;
  platform: number;
  queues: Place[];
}
export interface RailLayout {
  id?: string;
  name?: string;
  branches?: RailLayout[];
  sections: RailSection[];
  stations: RailStation[];
  length: number;
}
export const railLayouts = (layout?: RailLayout): RailLayout[] =>
  layout ? [layout, ...(layout.branches ?? [])] : [];
export const railStations = (layout?: RailLayout): RailStation[] =>
  railLayouts(layout).flatMap((line) => line.stations);
export interface RailDefinition {
  id: string;
  name: string;
  points: Vec[];
  stops: [string, string, number, number][];
}
export function railSample(
  layout: RailLayout,
  distance: number,
): { point: Vec; direction: Vec; section: RailSection } {
  const s = ((distance % layout.length) + layout.length) % layout.length;
  let low = 0,
    high = layout.sections.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    const section = layout.sections[mid];
    if (section.start + section.length <= s) low = mid + 1;
    else high = mid;
  }
  const section = layout.sections[low],
    t = (s - section.start) / section.length;
  const direction = {
    x: (section.b.x - section.a.x) / section.length,
    y: 0,
    z: (section.b.z - section.a.z) / section.length,
  };
  return {
    point: {
      x: section.a.x + (section.b.x - section.a.x) * t,
      y: RAIL_Y,
      z: section.a.z + (section.b.z - section.a.z) * t,
    },
    direction,
    section,
  };
}
/** A finite, supported, cell-authored viaduct. Curves share the same route samples as rolling stock. */
export function prepareRail(city: City, definition?: RailDefinition): City {
  if (!city.regional || (!definition && city.rail)) return city;
  if (definition && railLayouts(city.rail).some((line) => line.id === definition.id)) return city;
  const all = [
    ...city.buildings,
    ...(city.props ?? []),
    ...(city.bridges ?? []),
    ...(city.infrastructure ?? []),
  ];
  let serial = Math.max(...all.map((b) => b.id)) + 1;
  const points: Vec[] = [];
  const line = (ax: number, az: number, bx: number, bz: number) => {
    const n = Math.ceil(Math.hypot(bx - ax, bz - az) / 28);
    for (let i = 0; i < n; i++)
      points.push({ x: ax + ((bx - ax) * i) / n, y: RAIL_Y, z: az + ((bz - az) * i) / n });
  };
  const arc = (x: number, z: number, from: number) => {
    for (let i = 0; i < 8; i++) {
      const a = from + (i * Math.PI) / 16;
      points.push({ x: x + 35 * Math.cos(a), y: RAIL_Y, z: z + 35 * Math.sin(a) });
    }
  };
  line(-235, -270, 255, -270);
  arc(255, -235, -Math.PI / 2);
  line(290, -235, 290, 315);
  arc(255, 315, 0);
  line(255, 350, -235, 350);
  arc(-235, 315, Math.PI / 2);
  line(-270, 315, -270, -235);
  arc(-235, -235, Math.PI);
  if (definition) points.splice(0, points.length, ...definition.points);
  const layout: RailLayout = {
    id: definition?.id ?? 'coastal',
    name: definition?.name ?? 'Coastal Line',
    sections: [],
    stations: [],
    length: 0,
  };
  if (city.rail) (city.rail.branches ??= []).push(layout);
  else city.rail = layout;
  const roadClear = (x: number, z: number) =>
    !city.roadGraph!.edges.some((e) => {
      const a = city.roadGraph!.nodes[e.from],
        b = city.roadGraph!.nodes[e.to],
        dx = b.x - a.x,
        dz = b.z - a.z;
      const t = Math.max(0, Math.min(1, ((x - a.x) * dx + (z - a.z) * dz) / (dx * dx + dz * dz)));
      return Math.hypot(x - a.x - dx * t, z - a.z - dz * t) < e.width / 2 + 1.8;
    });
  const cell = (
    b: Building,
    x: number,
    y: number,
    z: number,
    material: Cell['material'],
    role: Cell['role'],
    color: number,
    bounds?: Cell['bounds'],
  ) =>
    b.cells.push({
      id: b.id * 1e6 + key(x, y, z),
      x,
      y,
      z,
      material,
      role,
      color,
      hp: MATERIALS[material].strength * 2,
      root: y === 0,
      bounds,
    });
  for (let i = 0; i < points.length; i++) {
    const a = points[i],
      b = points[(i + 1) % points.length],
      length = Math.hypot(b.x - a.x, b.z - a.z),
      dx = (b.x - a.x) / length,
      dz = (b.z - a.z) / length,
      angle = -Math.atan2(dz, dx),
      id = serial++;
    const deck: Building = {
      id,
      name: `${layout.name} viaduct`,
      kind: 'bridge',
      category: 'bridge',
      x: a.x + dz * 2.5,
      z: a.z - dx * 2.5,
      baseY: GROUND,
      w: Math.ceil(length / CELL),
      d: 4,
      h: 7,
      color: 0xb7bcb1,
      accent: 0x679c94,
      cells: [],
      rotation: { x: 0, y: Math.sin(angle / 2), z: 0, w: Math.cos(angle / 2) },
    };
    const guide: number[] = [];
    for (let x = 0; x < deck.w; x++)
      for (let z = 0; z < 4; z++) {
        const width = Math.min(1, length / CELL - x);
        cell(deck, x, 5, z, 'concrete', 'beam', 0xb2b6ad, { x: 0, y: 0, z: 0, w: width, h: 1, d: 1 });
        if (z === 1 || z === 2) {
          cell(deck, x, 6, z, 'metal', 'beam', 0x677e80, {
            x: 0,
            y: 0,
            z: z === 1 ? 0.39 : 0.51,
            w: width,
            h: 0.12 / CELL,
            d: 0.1,
          });
          guide.push(deck.id * 1e6 + key(x, 5, z));
        } else if (x % 2 === 0)
          cell(deck, x, 6, z, 'wood', 'prop', 0x8d8d7d, {
            x: 0,
            y: 0,
            z: 0,
            w: Math.min(width, 0.3),
            h: 0.06,
            d: 1,
          });
      }
    for (const x of [0, deck.w - 1]) {
      const along = (x + 0.5) * CELL;
      if (!roadClear(a.x + dx * along, a.z + dz * along)) continue;
      for (const z of [1, 2]) for (let y = 0; y < 5; y++) cell(deck, x, y, z, 'concrete', 'column', 0xb6beb1);
    }
    if (!deck.cells.some((c) => c.root)) {
      if (definition) {
        // The new service follows reserved road corridors. A physical portal
        // carries it over the traffic; its columns stand outside both lanes.
        let support = -1,
          portalDepth = 12;
        for (const depth of [12, 16, 20, 24]) {
          const lateral = ((depth - 1) * CELL) / 2;
          for (let xx = 0; xx < deck.w; xx++) {
            const along = (xx + 0.5) * CELL;
            if (
              [-1, 1].every((sign) =>
                roadClear(a.x + dx * along - dz * lateral * sign, a.z + dz * along + dx * lateral * sign),
              )
            ) {
              support = xx;
              portalDepth = depth;
              break;
            }
          }
          if (support >= 0) break;
        }
        if (support >= 0) {
          const offset = (portalDepth - 4) / 2;
          deck.x += dz * offset * CELL;
          deck.z -= dx * offset * CELL;
          deck.d = portalDepth;
          for (const c of deck.cells) {
            c.z += offset;
            c.id = deck.id * 1e6 + key(c.x, c.y, c.z);
          }
          for (let j = 0; j < guide.length; j++) guide[j] += offset * 32;
          for (let zz = 0; zz < portalDepth; zz++) cell(deck, support, 4, zz, 'metal', 'beam', 0x7d9290);
          for (const zz of [0, portalDepth - 1])
            for (let yy = 0; yy < 4; yy++)
              cell(deck, support, yy, zz, 'concrete', 'column', 0xb6beb1, {
                x: 0.2,
                y: 0,
                z: 0.3,
                w: 0.6,
                h: 1,
                d: 0.4,
              });
        }
      }
    }
    if (!deck.cells.some((c) => c.root)) {
      // At a road crossing, carry the short curved span from a real pier behind
      // the road. The connecting beam is occupied geometry and can fail too.
      let offset = 0;
      for (let n = 3; n + deck.w < 32; n++)
        if (roadClear(a.x - dx * (n - 0.5) * CELL, a.z - dz * (n - 0.5) * CELL)) {
          offset = n;
          break;
        }
      if (!offset) throw new Error(`Rail section ${i} has no clear pier foundation`);
      deck.x -= dx * offset * CELL;
      deck.z -= dz * offset * CELL;
      deck.w += offset;
      for (const c of deck.cells) {
        c.x += offset;
        c.id = deck.id * 1e6 + key(c.x, c.y, c.z);
      }
      for (let j = 0; j < guide.length; j++) guide[j] += offset;
      for (let xx = 0; xx <= offset; xx++)
        for (const zz of [1, 2]) cell(deck, xx, 4, zz, 'metal', 'beam', 0x859c96);
      for (let yy = 0; yy < 4; yy++)
        for (const zz of [1, 2]) cell(deck, 0, yy, zz, 'concrete', 'column', 0xb6beb1);
    }
    city.infrastructure!.push(deck);
    layout.sections.push({ id: i, a, b, start: layout.length, length, buildingId: id, cells: guide });
    layout.length += length;
  }
  const roadSurface = new TrafficSurface(city);
  const stops =
    definition?.stops ??
    ([
      ['central', 'Central Exchange', -60, -270],
      ['airport', 'Alder Airport', 290, -160],
      ['eastbank', 'Eastbank Gardens', 290, 180],
      ['coast', 'Linden Waterfront', -60, 350],
      ['campus', 'Westmere Campus', -270, -30],
    ] as const);
  for (const [slug, name, x, z] of stops) {
    let distance = 0,
      best = Infinity;
    for (const s of layout.sections) {
      const dx = s.b.x - s.a.x,
        dz = s.b.z - s.a.z,
        t = Math.max(0, Math.min(1, ((x - s.a.x) * dx + (z - s.a.z) * dz) / (s.length * s.length))),
        d = Math.hypot(s.a.x + dx * t - x, s.a.z + dz * t - z);
      if (d < best) {
        best = d;
        distance = s.start + s.length * t;
      }
    }
    // Keep the ground-level station plaza and lift footprint off active roads.
    // A raised railway may cross a street; its entrance cannot occupy that lane.
    for (const offset of [0, -24, 24, -48, 48, -72, 72]) {
      const sample = railSample(layout, distance + offset),
        side = { x: -sample.direction.z, z: sample.direction.x };
      let clear = true;
      for (let along = -18; along <= 19; along += 2)
        for (const lateral of definition ? [6.5, 7.175, 8.8] : [1.55, 4.7, 7.8]) {
          if (
            roadSurface.contains(
              {
                x: sample.point.x + sample.direction.x * along + side.x * lateral,
                y: GROUND + 0.2,
                z: sample.point.z + sample.direction.z * along + side.z * lateral,
              },
              -0.5,
            )
          )
            clear = false;
        }
      if (clear) {
        distance += offset;
        break;
      }
    }
    const { point, direction } = railSample(layout, distance),
      side = { x: -direction.z, y: 0, z: direction.x },
      angle = -Math.atan2(direction.z, direction.x),
      id = serial++,
      top = RAIL_Y + 0.52,
      platformDepth = definition ? 6 : 4,
      liftOffset = definition ? 2 : 0;
    const platform: Building = {
      id,
      name: `${name} station`,
      kind: 'civic',
      category: 'building',
      x: point.x - direction.x * 18 + side.x * 1.55,
      z: point.z - direction.z * 18 + side.z * 1.55,
      baseY: GROUND,
      w: 29,
      d: platformDepth,
      h: 10,
      color: 0xcad0bf,
      accent: 0x629e99,
      cells: [],
      rotation: { x: 0, y: Math.sin(angle / 2), z: 0, w: Math.cos(angle / 2) },
    };
    for (let xx = 0; xx < 29; xx++)
      for (let zz = 0; zz < platformDepth; zz++)
        cell(platform, xx, 6, zz, 'concrete', 'slab', zz === 0 ? 0xe3c783 : 0xd3d4c8, {
          x: 0,
          y: 0,
          z: 0,
          w: 1,
          h: (top - GROUND - 6 * CELL) / CELL,
          d: 1,
        });
    for (const xx of [0, 14, 28])
      for (const zz of definition ? [4, 5] : [0, 3])
        // The county platform's full-width beam occupies level five itself.
        // Do not author an overlapping column with the same stable cell ID.
        for (let yy = 0; yy < (definition ? 5 : 6); yy++)
          cell(platform, xx, yy, zz, 'concrete', 'column', 0xb2baae, {
            x: 0.3,
            y: 0,
            z: 0.3,
            w: 0.4,
            h: 1,
            d: 0.4,
          });
    if (definition)
      for (const xx of [0, 14, 28])
        for (let zz = 0; zz < platformDepth; zz++) cell(platform, xx, 5, zz, 'metal', 'beam', 0x80948d);
    for (const xx of [3, 8, 14, 20, 25])
      for (let yy = 7; yy < 9; yy++) {
        cell(platform, xx, yy, 3, 'metal', 'column', 0x6b8a83, {
          x: 0.45,
          y: 0,
          z: 0.7,
          w: 0.1,
          h: 1,
          d: 0.1,
        });
        if (definition && xx === 3) {
          const post = platform.cells.at(-1)!;
          // The shifted lift and canopy have separate occupied posts in this
          // grid cell. Both remain visible, collidable and damageable together.
          post.parts = [
            { ...post.bounds!, color: post.color },
            { x: 0.8, y: 0, z: 0.8, w: 0.1, h: 1, d: 0.1, color: 0x637c79 },
          ];
          post.bounds = undefined;
        }
      }
    for (const xx of [7, 8, 9, 10, 11, 12, 18, 19, 20, 21, 22, 23])
      for (let zz = 2; zz < 4; zz++)
        cell(platform, xx, 9, zz, 'metal', 'roof', 0x719d94, { x: 0, y: 0, z: 0, w: 1, h: 0.12, d: 1 });
    for (const xx of [1, 3])
      for (const zz of [1, 3])
        for (let yy = 0; yy < 10; yy++)
          if (
            yy !== 6 &&
            !(xx === 3 && zz === 3 && (yy === 7 || yy === 8)) &&
            !(definition && xx === 3 && zz === 1 && (yy === 7 || yy === 8))
          )
            cell(platform, xx, yy, zz + liftOffset, 'metal', 'column', 0x637c79, {
              x: 0.8,
              y: 0,
              z: 0.8,
              w: 0.1,
              h: 1,
              d: 0.1,
            });
    for (let yy = 0; yy < 9; yy++)
      if (yy !== 6)
        cell(platform, 1, yy, 2 + liftOffset, 'glass', 'glass', 0x9ac3bc, {
          x: 0.86,
          y: 0,
          z: 0,
          w: 0.06,
          h: 1,
          d: 1,
        });
    for (let xx = 4; xx < 28; xx++)
      if (![8, 14, 20, 25].includes(xx))
        cell(platform, xx, 7, 3, 'metal', 'prop', 0x587a73, {
          x: 0,
          y: 0.28,
          z: 0.93,
          w: 1,
          h: 0.06,
          d: 0.06,
        });
    const plaza: Building = {
      ...platform,
      id: serial++,
      name: `${name} entrance plaza`,
      kind: 'paving',
      category: 'prop',
      h: 1,
      cells: [],
    };
    for (let xx = 0; xx < 29; xx++)
      for (let zz = definition ? 3 : 0; zz < platformDepth + 1; zz++)
        cell(plaza, xx, 0, zz, 'concrete', 'slab', xx % 4 === 0 ? 0xacc0b1 : 0xc8cfc0, {
          x: 0,
          y: 0,
          z: 0,
          w: 1,
          h: 0.04,
          d: 1,
        });
    plaza.d = platformDepth + 1;
    city.props!.push(plaza);
    const local = (xx: number, zz: number, y: number): Vec => ({
      x: platform.x + direction.x * xx * CELL + side.x * zz * CELL,
      y,
      z: platform.z + direction.z * xx * CELL + side.z * zz * CELL,
    });
    const lift = local(2.5, 2.5 + liftOffset, top),
      entrance = { ...lift, y: GROUND };
    const station: RailStation = {
      service: layout.id,
      id: `station:${slug}`,
      name,
      distance,
      point,
      direction,
      side,
      platform: id,
      lift,
      entrance,
      queues: [],
    };
    for (let i = 0; i < 24; i++) {
      const xx = 3 + i,
        zz = 2;
      const p: Place = {
        id: `rail-queue:${slug}:${i}`,
        kind: 'stop',
        name: `${name} · ${layout.name}`,
        ...local(xx + 0.5, zz + 0.5, top),
        buildingId: id,
        floor: 0,
        supportCell: id * 1e6 + key(xx, 6, zz),
        capacity: 1,
        duration: 0,
      };
      station.queues.push(p);
      (city.places ??= []).push(p);
    }
    city.buildings.push(platform);
    layout.stations.push(station);
  }
  layout.stations.sort((a, b) => a.distance - b.distance);
  return city;
}
